import { captureConversationAccess } from 'dashboard/permissionSets/conversationReads';

const requests = new WeakMap();
const windows = new WeakMap();

export const captureConversationHistoryWindow = (
  context,
  conversationId,
  replace = false
) => {
  if (!windows.has(context.rootState))
    windows.set(context.rootState, new Map());
  const active = windows.get(context.rootState);
  if (replace || !active.has(conversationId)) active.set(conversationId, {});
  const window = active.get(conversationId);
  const isCurrent = captureConversationAccess(context, conversationId);
  return () =>
    isCurrent() &&
    context.state.allConversations.some(item => item.id === conversationId) &&
    windows.get(context.rootState)?.get(conversationId) === window;
};

export const clearConversationHistoryWindows = rootState => {
  windows.delete(rootState);
};

// Route selection and access revalidation may request the same page together.
// A request from an invalidated access revision must never be shared again.
export const withConversationHistoryRequest = (context, params, load) => {
  if (!requests.has(context.rootState)) {
    requests.set(context.rootState, new Map());
  }
  const pending = requests.get(context.rootState);
  const key = JSON.stringify([
    params.type,
    params.conversationId,
    params.before,
    params.after,
    params.anchorId,
  ]);
  const existing = pending.get(key);
  if (existing?.isCurrent()) return existing.promise;

  const accessIsCurrent = captureConversationAccess(
    context,
    params.conversationId
  );
  const request = {};
  request.isCurrent = () =>
    accessIsCurrent() &&
    windows.get(context.rootState)?.get(params.conversationId) ===
      request.window;
  request.promise = (async () => {
    try {
      return await load(request.isCurrent);
    } finally {
      if (pending.get(key) === request) pending.delete(key);
    }
  })();
  // load starts synchronously and establishes the window before its first await.
  request.window = windows.get(context.rootState)?.get(params.conversationId);
  pending.set(key, request);
  return request.promise;
};

export const prependUniqueMessages = (messages, page) => {
  const ids = new Set(messages.map(message => message.id));
  const older = page.filter(message => {
    if (ids.has(message.id)) return false;
    ids.add(message.id);
    return true;
  });
  messages.unshift(...older);
};

export const mergeHistoryWindow = (existing, page, hasMoreAfter) => {
  const current = new Map(existing.map(message => [message.id, message]));
  const merged = new Map();
  page.forEach(message => {
    merged.set(message.id, current.get(message.id) || message);
  });
  const lastId = Math.max(...page.map(message => message.id));
  const echoes = new Set(page.map(message => message.echo_id));
  existing.forEach(message => {
    const pending = typeof message.id !== 'number';
    if (
      !echoes.has(message.id) &&
      (pending || (page.length && !hasMoreAfter && message.id > lastId))
    ) {
      merged.set(message.id, message);
    }
  });
  return [...merged.values()].sort((a, b) => {
    if (typeof a.id !== 'number') return typeof b.id !== 'number' ? 0 : 1;
    if (typeof b.id !== 'number') return -1;
    return a.created_at - b.created_at || a.id - b.id;
  });
};
